import { useTranslation } from 'react-i18next';
import { useScrollReveal } from '@/hooks/useScrollReveal';

const couches = [
  { num: '1', key: 'bicouche_couche_1', sub: 'bicouche_couche_1_sub', color: 'bg-slate-primary' },
  { num: '2', key: 'bicouche_couche_2', sub: 'bicouche_couche_2_sub', color: 'bg-slate-light' },
  { num: '3', key: 'bicouche_couche_3', sub: 'bicouche_couche_3_sub', color: 'bg-copper' },
  { num: '4', key: 'bicouche_couche_4', sub: 'bicouche_couche_4_sub', color: 'bg-copper-dark' },
  { num: '5', key: 'bicouche_couche_5', sub: 'bicouche_couche_5_sub', color: 'bg-soft-dark' },
];

export default function Bicouche() {
  const { t } = useTranslation('toitplat');
  const { ref, isVisible } = useScrollReveal<HTMLElement>(0.1);

  return (
    <section ref={ref} id="bicouche" className="bg-soft-gray py-16 md:py-24">
      <div className="max-w-6xl mx-auto px-4 md:px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-14 items-center">
          {/* Diagramme */}
          <div
            className={`transition-all duration-1000 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}
          >
            <div className="bg-white rounded-xl p-6 md:p-8 shadow-sm">
              <h3 className="font-manrope font-bold text-slate-primary text-lg mb-6 text-center">
                Système bicouche — 5 couches
              </h3>
              <div className="space-y-2">
                {couches.map((c, idx) => (
                  <div
                    key={idx}
                    className={`flex items-center gap-4 p-3 md:p-4 rounded-lg transition-all duration-700 ${
                      isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'
                    } ${c.color}`}
                    style={{ transitionDelay: `${idx * 120 + 200}ms` }}
                  >
                    <span className="w-8 h-8 flex-shrink-0 flex items-center justify-center rounded-full bg-white/20 text-white font-bold text-sm">
                      {c.num}
                    </span>
                    <div>
                      <p className="text-white font-semibold text-sm">{t(c.key)}</p>
                      <p className="text-white/70 text-xs">{t(c.sub)}</p>
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-center text-xs text-slate-primary/40 mt-4">
                De bas en haut — coupe technique simplifiée
              </p>
            </div>
          </div>

          {/* Texte */}
          <div
            className={`transition-all duration-1000 delay-200 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}
          >
            <h2 className="font-manrope font-bold text-slate-primary text-2xl md:text-3xl lg:text-4xl mb-5 leading-tight">
              {t('bicouche_h2')}
            </h2>
            <p className="text-slate-primary/70 text-base md:text-lg leading-relaxed">
              {t('bicouche_text')}
            </p>

            <div className="mt-8 rounded-xl overflow-hidden">
              <img
                src="https://readdy.ai/api/search-image?query=Close-up%20macro%20photography%20of%20professional%20roofer%20using%20propane%20torch%20to%20weld%20elastomeric%20membrane%20layers%20on%20flat%20roof%2C%20orange%20flame%20visible%2C%20black%20bituminous%20membrane%20texture%2C%20Quebec%20construction%20site%2C%20dramatic%20warm%20lighting%2C%20professional%20documentary%20style%2C%20detailed%20workmanship%20shot%2C%20no%20text&width=800&height=600&seq=toitplat-bicouche1&orientation=landscape"
                alt="Soudure membrane élastomère au chalumeau à Lévis par couvreur spécialisé RBQ"
                className="w-full h-56 md:h-72 object-cover object-top"
                loading="lazy"
                width="800"
                height="600"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}